import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { SecondaryButton } from '@components/Buttons/SecondaryButton';
import { Box, makeStyles } from '@material-ui/core';
import { useThemeColor } from '@themes/Colors';
import React from 'react';
import clsx from 'clsx';
import { IStepper } from '@hooks/useStepper';
import { IValueState } from '@hooks/useValueState';
import { IValidarDocumentoResponse } from '@services/ContratoClienteService';
import { IForm } from '@hooks/useForm';
import { ICliente } from '@model/Cliente';
import { useValidation } from '@hooks/useValidation';
import { yup } from '@validations/Yup';
import { EnumClienteTipo } from '@model/ClienteTipo';

export type ClienteCommandProps = {
    stepper: IStepper
    clienteValueState: IValueState<IValidarDocumentoResponse>
    form: IForm<Partial<ICliente>>
    onPrev?: VoidFunction
    onNext?: VoidFunction
    onCancel?: VoidFunction
}

export function ClienteCommand ({stepper, clienteValueState, form, onPrev, onNext, onCancel}: ClienteCommandProps) {
    const cls = useStyles();
    const themeColor = useThemeColor();

    const searched = clienteValueState.value !== undefined && clienteValueState.value?.cliente !== null;
    const clienteFound = !!clienteValueState.value?.cliente;

    const handleCancel = React.useCallback(() => {
        clienteValueState.set({
            cliente: null,
            possuiDebitos: 0
        });

        form.reset();
        if (onCancel !== undefined) {
            onCancel();
        }

    }, [clienteValueState, form, onCancel]);

    const validation = useValidation<ICliente>({
        nome: yup.string().required(),
        rg: yup.string().when(["clienteTipoId"], {
            is: (clienteTipoId: EnumClienteTipo) => clienteTipoId === EnumClienteTipo.PessoaFisica,
            then: yup.string().required()
        }),
        whatsappEnvio: yup.string().required(),
        spc: yup.string().booleanString(),
        enderecoCep: yup.string().required(),
        enderecoLogradouro: yup.string().required(),
        enderecoBairro: yup.string().required(),
        enderecoCidade: yup.string().required(),
        enderecoUf: yup.string().required(),
    });

    const handlePrev = React.useCallback(() => {
        form.saveValues();
        stepper.prev();
        if (onPrev !== undefined) {
            onPrev();
        }
    }, [form, onPrev, stepper]);

    const next = React.useCallback(() => {
        stepper.next();

        if (onNext !== undefined) {
            onNext();
        }
    }, [onNext, stepper]);

    const handleNext = React.useCallback(() => {
        if (clienteFound) {
            next();
        }
        else {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(() => {
                    form.saveValues();
                    next();
                })
                .catch(form.setErrorsFromYup);
        }
    }, [clienteFound, form, next, validation]);

    return (
        <Box className={cls.box}>
            <HorizontalGrid className={clsx(themeColor.component.commandPanel, cls.grid)}>
                <GridItem>
                    <PrimaryButton onClick={handlePrev}>Voltar</PrimaryButton>
                </GridItem>

                {
                    searched && (
                        <GridItem floatRight>
                            <HorizontalGrid spacing={1} verticalAlign="center">
                                <React.Fragment>
                                    <GridItem>
                                        <SecondaryButton onClick={handleCancel}>Cancelar</SecondaryButton>
                                    </GridItem>

                                    <GridItem>
                                        <PrimaryButton onClick={handleNext}>Avançar</PrimaryButton>
                                    </GridItem>
                                </React.Fragment>
                            </HorizontalGrid>
                        </GridItem>
                    )
                }
            </HorizontalGrid>
        </Box>
    )
}

const useStyles = makeStyles(theme => ({
    box: {
        marginTop: theme.spacing(2)
    },

    grid: {
        padding: `${theme.spacing(1)}px ${theme.spacing(4)}px`
    }
}));